import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Mail, Send, Bot, User, Phone, MessageSquare, StickyNote } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { toast as sonnerToast } from "sonner";
import { CallMonitorPanel } from "@/components/call-monitor/CallMonitorPanel";
import { useCallMonitorContext } from "@/contexts/CallMonitorContext";

interface JobCommunicationsProps {
  jobId: string;
  customerEmail?: string | null;
  customerName?: string | null;
  customerPhone?: string | null;
  onAddNote?: () => void;
}

export const JobCommunications = ({ jobId, customerEmail, customerName, customerPhone, onAddNote }: JobCommunicationsProps) => {
  const [emails, setEmails] = useState<any[]>([]);
  const [callNotes, setCallNotes] = useState<any[]>([]);
  const [isComposing, setIsComposing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const { toast } = useToast();
  const { user, orgId } = useAuth();
  const { setContext: setCallContext, clearContext: clearCallContext } = useCallMonitorContext();

  const [composeForm, setComposeForm] = useState({
    subject: "",
    body: "",
  });

  // Set call monitor context for this job
  useEffect(() => {
    if (jobId) {
      setCallContext({
        activeJobId: jobId,
        activeLeadId: null,
        callMode: 'job'
      });
    }
    
    return () => {
      clearCallContext();
    };
  }, [jobId, setCallContext, clearCallContext]);

  useEffect(() => {
    fetchJobEmails();
    fetchCallNotes();

    // Subscribe to realtime updates
    const emailChannel = supabase
      .channel(`job-emails-${jobId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'emails', filter: `job_id=eq.${jobId}` },
        () => fetchJobEmails()
      )
      .subscribe();

    const notesChannel = supabase
      .channel(`job-call-notes-${jobId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'crm_notes', filter: `job_id=eq.${jobId}` },
        () => fetchCallNotes()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(emailChannel);
      supabase.removeChannel(notesChannel);
    };
  }, [jobId]);

  const fetchJobEmails = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("emails")
        .select("*")
        .eq("job_id", jobId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setEmails(data || []);
    } catch (error: any) {
      toast({
        title: "Error loading communications",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCallNotes = async () => {
    try {
      const { data, error } = await supabase
        .from("crm_notes")
        .select("*")
        .eq("job_id", jobId)
        .eq("note_type", "call")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setCallNotes(data || []);
    } catch (error: any) {
      console.error("Error loading call notes:", error);
    }
  };

  const generateAIEmail = async () => {
    if (!customerEmail) {
      toast({
        title: "No customer email",
        description: "Please add a customer email to this job first",
        variant: "destructive",
      });
      return;
    }

    setAiLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("email-ai-composer", {
        body: {
          jobId,
          recipientEmail: customerEmail,
          recipientName: customerName,
          context: composeForm.subject || "general update",
        },
      });

      if (error) throw error;

      setComposeForm({
        subject: data.subject || composeForm.subject,
        body: data.body || "",
      });

      toast({
        title: "AI Draft Ready",
        description: "Review and send the AI-generated email",
      });
    } catch (error: any) {
      toast({
        title: "AI Generation Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setAiLoading(false);
    }
  };

  const sendEmail = async () => {
    if (!customerEmail) {
      toast({
        title: "No customer email",
        description: "Please add a customer email to this job first",
        variant: "destructive",
      });
      return;
    }

    try {
      if (!user || !orgId) return;

      const userEmail = user.email || "";

      // Send actual email via Resend
      const { error: sendError } = await supabase.functions.invoke('send-email', {
        body: {
          to: customerEmail,
          subject: composeForm.subject,
          html: `<p>${composeForm.body.replace(/\n/g, '<br>')}</p>`,
          text: composeForm.body,
          from: 'RoofGenius AI <no-reply@resend.dev>',
          replyTo: userEmail,
        },
      });

      if (sendError) throw sendError;

      // Save to database
      const { error } = await supabase.from("emails").insert({
        org_id: orgId,
        job_id: jobId,
        subject: composeForm.subject,
        from_email: userEmail,
        to_email: [customerEmail],
        body_text: composeForm.body,
        body_html: `<p>${composeForm.body.replace(/\n/g, '<br>')}</p>`,
        folder: "sent",
        sent_at: new Date().toISOString(),
        ai_generated: aiLoading,
      });

      if (error) throw error;

      toast({
        title: "Email sent",
        description: `Email sent to ${customerName || customerEmail}`,
      });

      setIsComposing(false);
      setComposeForm({ subject: "", body: "" });
      fetchJobEmails();
    } catch (error: any) {
      toast({
        title: "Error sending email",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleCall = () => {
    if (!customerPhone) {
      sonnerToast.error("No phone number on file");
      return;
    }
    window.location.href = `tel:${customerPhone}`;
    sonnerToast.success("Opening dialer...");
  };

  const handleSMS = () => {
    if (!customerPhone) {
      sonnerToast.error("No phone number on file");
      return;
    }
    window.location.href = `sms:${customerPhone}`;
    sonnerToast.success("Opening SMS...");
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Call with Bridgette */}
      <CallMonitorPanel jobId={jobId} mode="job" />

      {/* Phone Calls Section */}
      {callNotes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <Phone className="w-5 h-5" />
              Phone Calls
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px]">
              <div className="space-y-3">
                {callNotes.map((note) => (
                  <Card key={note.id} className="border-l-4 border-l-primary">
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-primary" />
                          <span className="font-medium">Call with {customerName || "Customer"}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {note.created_at && formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      
                      {note.ai_summary && (
                        <div className="mb-3 p-3 bg-muted rounded-lg">
                          <div className="text-sm font-medium mb-1 flex items-center gap-2">
                            <Bot className="w-4 h-4" />
                            AI Summary
                          </div>
                          <p className="text-sm text-muted-foreground">{note.ai_summary}</p>
                        </div>
                      )}
                      
                      <div className="space-y-2">
                        <div className="text-sm">
                          <strong>Full Transcript:</strong>
                          <div className="mt-1 p-2 bg-muted/50 rounded text-xs whitespace-pre-wrap max-h-[200px] overflow-y-auto">
                            {note.content}
                          </div>
                        </div>
                        
                        {note.ai_extracted_info && (
                          <div className="text-sm">
                            <strong>Key Details:</strong>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {Object.entries(note.ai_extracted_info as Record<string, any>).map(([key, value]) => (
                                <Badge key={key} variant="secondary" className="text-xs">
                                  {key}: {String(value)}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Quick Contact Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm md:text-base">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="gap-2"
              onClick={handleCall}
            >
              <Phone className="w-4 h-4" />
              {customerPhone ? "Call" : "No Phone"}
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="gap-2"
              onClick={handleSMS}
            >
              <MessageSquare className="w-4 h-4" />
              SMS
            </Button>
          </div>
          {onAddNote && (
            <Button 
              variant="outline" 
              size="sm" 
              className="gap-2 w-full"
              onClick={onAddNote}
            >
              <StickyNote className="w-4 h-4" />
              Add Note
            </Button>
          )}
          {customerEmail && (
            <div className="text-sm text-muted-foreground pt-2 border-t">
              <strong>Email:</strong> {customerEmail}
            </div>
          )}
          {customerPhone && (
            <div className="text-sm text-muted-foreground">
              <strong>Phone:</strong> {customerPhone}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Email Communications
            </CardTitle>
            <Button onClick={() => setIsComposing(!isComposing)}>
              {isComposing ? "Cancel" : "New Email"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isComposing && (
            <Card className="mb-6 border-2 border-primary">
              <CardContent className="pt-6 space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">To:</label>
                  <Input value={customerEmail || "No customer email"} disabled />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Subject:</label>
                  <Input
                    placeholder="Email subject"
                    value={composeForm.subject}
                    onChange={(e) => setComposeForm({ ...composeForm, subject: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Message:</label>
                  <Textarea
                    placeholder="Email message"
                    rows={8}
                    value={composeForm.body}
                    onChange={(e) => setComposeForm({ ...composeForm, body: e.target.value })}
                  />
                </div>
                <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={generateAIEmail}
                    disabled={aiLoading || !customerEmail}
                  >
                    <Bot className="w-4 h-4 mr-2" />
                    {aiLoading ? "Generating..." : "Draft with AI"}
                  </Button>
                  <Button onClick={sendEmail} disabled={!composeForm.subject || !composeForm.body}>
                    <Send className="w-4 h-4 mr-2" />
                    Send Email
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="space-y-4">
            <h3 className="font-semibold">Email History</h3>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : emails.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No communications yet. Send your first email to the customer!
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {emails.map((email) => (
                    <Card key={email.id}>
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {email.folder === "sent" ? (
                              <User className="w-4 h-4 text-primary" />
                            ) : (
                              <Mail className="w-4 h-4" />
                            )}
                            <span className="font-medium">{email.subject}</span>
                          </div>
                          {email.ai_generated && (
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <Bot className="w-3 h-3" />
                              AI
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground mb-2">
                          {email.folder === "sent" ? "To" : "From"}: {email.from_email}
                          <span className="ml-2">
                            {email.created_at && formatDistanceToNow(new Date(email.created_at), { addSuffix: true })}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{email.body_text}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
